import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import AddRoundedIcon from "@mui/icons-material/AddRounded"
import DeleteIcon from "@mui/icons-material/Delete"
import LockRoundedIcon from "@mui/icons-material/LockRounded"
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  approvePurchaseOrder,
  cancelPurchaseOrder,
  createPurchaseOrder,
  getPurchaseOrderFormData,
  getPurchaseOrders,
  sendPurchaseOrder,
} from "../../services/purchaseOrderService"


// =========================================================
// Helpers
// =========================================================

const today = () => new Date().toISOString().slice(0, 10)


const defaultItem = {
  product_id: "",
  ordered_quantity: 1,
  unit_cost: 0,
  discount_percent: 0,
  tax_percent: 0,
  notes: "",
  unit: "",
  product_name: "",
  product_code: "",
}


const defaultFormData = {
  organization_id: 1,

  supplier_id: "",
  warehouse_id: "",
  purchase_request_id: "",

  supplier_reference: "",

  order_date: today(),
  expected_delivery_date: today(),

  currency: "USD",
  exchange_rate: 1,

  discount_amount: 0,
  tax_amount: 0,
  transport_cost: 0,
  other_cost: 0,

  notes: "",

  items: [{ ...defaultItem }],
}


const getArray = (data, ...keys) => {
  for (const key of keys) {
    if (Array.isArray(data?.[key])) {
      return data[key]
    }
  }

  return []
}


const normalizeStatus = (status) =>
  String(status || "").toUpperCase()


const getApiErrorMessage = (
  error,
  fallback = "Une erreur est survenue."
) => {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  )
}


const formatNumber = (value, decimals = 2) => {
  const number = Number(value || 0)

  if (Number.isNaN(number)) {
    return "0.00"
  }

  return number.toFixed(decimals)
}


const buildItemFromPurchaseRequest = (item) => ({
  product_id: Number(item.product_id),

  ordered_quantity: Number(
    item.requested_quantity || 0
  ),

  unit_cost: Number(
    item.estimated_unit_cost || 0
  ),

  discount_percent: 0,
  tax_percent: 0,

  notes: item.notes || "",

  unit: item.unit || "",
  product_name: item.product_name || "",
  product_code: item.product_code || "",
})


// =========================================================
// Component
// =========================================================


function MetricCard({
  label,
  value,
  caption,
  accent = "#2563eb",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        minHeight: 100,
        borderRadius: 3.5,
        borderLeft: "4px solid",
        borderLeftColor: accent,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 800,
          letterSpacing: 0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,
          fontSize: { xs: 24, md: 28 },
          lineHeight: 1.15,
          fontWeight: 900,
          color: "#0f172a",
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.6 }}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  )
}

function PurchaseOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const sourcePurchaseRequestId =
    searchParams.get("purchase_request_id")

  const [orders, setOrders] = useState([])

  const [suppliers, setSuppliers] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [products, setProducts] = useState([])
  const [requests, setRequests] = useState([])
  const [organizations, setOrganizations] = useState([])

  const [
    selectedSourceRequest,
    setSelectedSourceRequest,
  ] = useState(null)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [open, setOpen] = useState(false)

  const [formData, setFormData] = useState({
    ...defaultFormData,
    items: [{ ...defaultItem }],
  })

  const [searchTerm, setSearchTerm] = useState("")

  const [page, setPage] = useState(0)
  const rowsPerPage = 5
  const [statusFilter, setStatusFilter] = useState("")

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // Pagination reset
  useEffect(() => {
    setPage(0)
  }, [searchTerm, statusFilter])

  // =======================================================
  // Snackbar
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  // =======================================================
  // Reset form
  // =======================================================

  const resetForm = () => {
    setSelectedSourceRequest(null)

    setFormData({
      ...defaultFormData,

      order_date: today(),
      expected_delivery_date: today(),

      items: [{ ...defaultItem }],
    })
  }


  // =======================================================
  // Apply purchase request
  // =======================================================

  const applyPurchaseRequest = (purchaseRequest) => {
    if (!purchaseRequest) {
      return
    }

    const requestItems = Array.isArray(
      purchaseRequest.items
    )
      ? purchaseRequest.items.map(
          buildItemFromPurchaseRequest
        )
      : []

    setSelectedSourceRequest(purchaseRequest)

    setFormData((prev) => ({
      ...prev,

      organization_id:
        purchaseRequest.organization_id ||
        prev.organization_id ||
        1,

      purchase_request_id: Number(
        purchaseRequest.id
      ),

      warehouse_id: Number(
        purchaseRequest.warehouse_id
      ),

      expected_delivery_date:
        purchaseRequest.required_date
          ? String(
              purchaseRequest.required_date
            ).slice(0, 10)
          : prev.expected_delivery_date,

      notes: purchaseRequest.reason
        ? `Demande ${purchaseRequest.code || ""} : ${
            purchaseRequest.reason
          }`
        : prev.notes,

      items:
        requestItems.length > 0
          ? requestItems
          : [{ ...defaultItem }],
    }))
  }


  // =======================================================
  // Load
  // =======================================================

  const loadData = async ({
    openSourceRequest = false,
  } = {}) => {
    try {
      setLoading(true)

      const formRequestId =
        sourcePurchaseRequestId || ""

      const [
        ordersData,
        form,
      ] = await Promise.all([
        getPurchaseOrders(),

        getPurchaseOrderFormData(
          formRequestId
            ? {
                purchase_request_id:
                  formRequestId,
              }
            : undefined
        ),
      ])

      setOrders(
        Array.isArray(ordersData)
          ? ordersData
          : []
      )

      setSuppliers(
        getArray(
          form,
          "suppliers",
          "active_suppliers"
        )
      )

      setWarehouses(
        getArray(
          form,
          "warehouses",
          "active_warehouses"
        )
      )

      setProducts(
        getArray(
          form,
          "products",
          "active_products"
        )
      )

      const approvedRequests = getArray(
        form,
        "purchase_requests",
        "approved_purchase_requests",
        "requests"
      )

      setRequests(approvedRequests)

      setOrganizations(
        getArray(
          form,
          "organizations",
          "active_organizations"
        )
      )

      const sourceRequest =
        form?.selected_purchase_request ||
        (
          sourcePurchaseRequestId
            ? approvedRequests.find(
                (request) =>
                  Number(request.id) ===
                  Number(
                    sourcePurchaseRequestId
                  )
              )
            : null
        )

      if (
        sourceRequest &&
        (
          openSourceRequest ||
          sourcePurchaseRequestId
        )
      ) {
        resetForm()

        applyPurchaseRequest(
          sourceRequest
        )

        setOpen(true)
      }
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          "Impossible de charger les bons de commande."
        ),
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    loadData({
      openSourceRequest: Boolean(
        sourcePurchaseRequestId
      ),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcePurchaseRequestId])


  // =======================================================
  // Open manually
  // =======================================================

  const handleOpen = async () => {
    resetForm()

    setSearchParams({})

    try {
      setLoading(true)

      const form =
        await getPurchaseOrderFormData()

      setSuppliers(
        getArray(
          form,
          "suppliers",
          "active_suppliers"
        )
      )

      setWarehouses(
        getArray(
          form,
          "warehouses",
          "active_warehouses"
        )
      )

      setProducts(
        getArray(
          form,
          "products",
          "active_products"
        )
      )

      setRequests(
        getArray(
          form,
          "purchase_requests",
          "approved_purchase_requests",
          "requests"
        )
      )

      setOrganizations(
        getArray(
          form,
          "organizations",
          "active_organizations"
        )
      )

      setOpen(true)
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          "Impossible de préparer le nouveau bon de commande."
        ),
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  // =======================================================
  // Close
  // =======================================================

  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)

    resetForm()

    if (sourcePurchaseRequestId) {
      setSearchParams({})
    }
  }


  // =======================================================
  // Main fields
  // =======================================================

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target

    const numericFields = [
      "organization_id",
      "supplier_id",
      "warehouse_id",
      "purchase_request_id",
      "exchange_rate",
      "discount_amount",
      "tax_amount",
      "transport_cost",
      "other_cost",
    ]

    setFormData((prev) => ({
      ...prev,

      [name]: numericFields.includes(name)
        ? value === ""
          ? ""
          : Number(value)
        : value,
    }))
  }


  // =======================================================
  // Select purchase request manually
  // =======================================================

  const handlePurchaseRequestChange = (
    event
  ) => {
    const value = event.target.value

    if (!value) {
      setSelectedSourceRequest(null)

      setFormData((prev) => ({
        ...prev,

        purchase_request_id: "",
        warehouse_id: "",

        items: [{ ...defaultItem }],
      }))

      return
    }

    const purchaseRequest =
      requests.find(
        (request) =>
          Number(request.id) ===
          Number(value)
      )

    if (!purchaseRequest) {
      return
    }

    applyPurchaseRequest(
      purchaseRequest
    )
  }


  // =======================================================
  // Items
  // =======================================================

  const handleItemChange = (
    index,
    field,
    value
  ) => {
    const numericFields = [
      "product_id",
      "ordered_quantity",
      "unit_cost",
      "discount_percent",
      "tax_percent",
    ]

    setFormData((prev) => ({
      ...prev,

      items: prev.items.map(
        (item, currentIndex) => {
          if (currentIndex !== index) {
            return item
          }

          const normalizedValue =
            numericFields.includes(field)
              ? value === ""
                ? ""
                : Number(value)
              : value

          let nextItem = {
            ...item,
            [field]: normalizedValue,
          }

          if (field === "product_id") {
            const selectedProduct =
              products.find(
                (product) =>
                  Number(product.id) ===
                  Number(value)
              )

            if (selectedProduct) {
              nextItem = {
                ...nextItem,

                unit:
                  selectedProduct.unit ||
                  "",

                product_name:
                  selectedProduct.name ||
                  "",

                product_code:
                  selectedProduct.code ||
                  "",
              }
            }
          }

          return nextItem
        }
      ),
    }))
  }


  const addItem = () => {
    if (selectedSourceRequest) {
      showSnackbar(
        "Les produits proviennent de la demande d'achat approuvée et sont verrouillés.",
        "info"
      )

      return
    }

    setFormData((prev) => ({
      ...prev,

      items: [
        ...prev.items,
        { ...defaultItem },
      ],
    }))
  }


  const removeItem = (index) => {
    if (selectedSourceRequest) {
      showSnackbar(
        "Vous ne pouvez pas supprimer un produit provenant de la demande d'achat approuvée.",
        "warning"
      )

      return
    }

    setFormData((prev) => {
      if (prev.items.length === 1) {
        return prev
      }

      return {
        ...prev,

        items: prev.items.filter(
          (_, currentIndex) =>
            currentIndex !== index
        ),
      }
    })
  }


  // =======================================================
  // Calculations
  // =======================================================

  const itemsSubtotal = useMemo(() => {
    return formData.items.reduce(
      (total, item) => {
        const quantity = Number(
          item.ordered_quantity || 0
        )

        const unitCost = Number(
          item.unit_cost || 0
        )

        const discountPercent = Number(
          item.discount_percent || 0
        )

        const taxPercent = Number(
          item.tax_percent || 0
        )

        const gross =
          quantity * unitCost

        const discount =
          gross *
          (
            discountPercent /
            100
          )

        const afterDiscount =
          gross - discount

        const tax =
          afterDiscount *
          (
            taxPercent /
            100
          )

        return (
          total +
          afterDiscount +
          tax
        )
      },
      0
    )
  }, [formData.items])


  const grandTotal = useMemo(() => {
    return (
      itemsSubtotal -
      Number(
        formData.discount_amount ||
        0
      ) +
      Number(
        formData.tax_amount ||
        0
      ) +
      Number(
        formData.transport_cost ||
        0
      ) +
      Number(
        formData.other_cost ||
        0
      )
    )
  }, [
    itemsSubtotal,
    formData.discount_amount,
    formData.tax_amount,
    formData.transport_cost,
    formData.other_cost,
  ])


  // =======================================================
  // Save
  // =======================================================

  const handleSave = async () => {
    if (!formData.supplier_id) {
      showSnackbar(
        "Sélectionnez un fournisseur.",
        "warning"
      )

      return
    }

    if (!formData.warehouse_id) {
      showSnackbar(
        "Sélectionnez l'entrepôt de réception.",
        "warning"
      )

      return
    }

    if (!formData.purchase_request_id) {
      showSnackbar(
        "Sélectionnez une demande d'achat approuvée.",
        "warning"
      )

      return
    }

    if (
      formData.items.some(
        (item) =>
          !item.product_id ||
          Number(
            item.ordered_quantity ||
            0
          ) <= 0
      )
    ) {
      showSnackbar(
        "Vérifiez les produits et les quantités commandées.",
        "warning"
      )

      return
    }

    if (
      formData.items.some(
        (item) =>
          Number(item.unit_cost || 0) < 0
      )
    ) {
      showSnackbar(
        "Le coût unitaire ne peut pas être négatif.",
        "warning"
      )

      return
    }

    if (
      formData.currency === "CDF" &&
      Number(
        formData.exchange_rate || 0
      ) <= 0
    ) {
      showSnackbar(
        "Renseignez un taux de change valide.",
        "warning"
      )

      return
    }

    const payload = {
      organization_id: Number(
        formData.organization_id || 1
      ),

      supplier_id: Number(
        formData.supplier_id
      ),

      warehouse_id: Number(
        formData.warehouse_id
      ),

      purchase_request_id: Number(
        formData.purchase_request_id
      ),

      supplier_reference:
        formData.supplier_reference
          ?.trim() || null,

      order_date:
        formData.order_date,

      expected_delivery_date:
        formData.expected_delivery_date,

      currency:
        formData.currency,

      exchange_rate: Number(
        formData.exchange_rate || 1
      ),

      discount_amount: Number(
        formData.discount_amount || 0
      ),

      tax_amount: Number(
        formData.tax_amount || 0
      ),

      transport_cost: Number(
        formData.transport_cost || 0
      ),

      other_cost: Number(
        formData.other_cost || 0
      ),

      notes:
        formData.notes?.trim() ||
        null,

      items: formData.items.map(
        (item) => ({
          product_id: Number(
            item.product_id
          ),

          ordered_quantity: Number(
            item.ordered_quantity
          ),

          unit_cost: Number(
            item.unit_cost || 0
          ),

          discount_percent: Number(
            item.discount_percent ||
            0
          ),

          tax_percent: Number(
            item.tax_percent || 0
          ),

          notes:
            item.notes?.trim() ||
            null,
        })
      ),
    }

    try {
      setSaving(true)

      const result =
        await createPurchaseOrder(
          payload
        )

      showSnackbar(
        result?.code
          ? `Bon de commande ${result.code} créé avec succès.`
          : "Bon de commande créé avec succès."
      )

      setOpen(false)

      resetForm()

      setSearchParams({})

      await loadData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          "Impossible de créer le bon de commande."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // Actions
  // =======================================================

  const runAction = async (
    action,
    message
  ) => {
    try {
      await action()

      showSnackbar(message)

      await loadData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getApiErrorMessage(
          error,
          "Opération impossible."
        ),
        "error"
      )
    }
  }


  // =======================================================
  // Filters
  // =======================================================

  const filteredOrders = useMemo(() => {
    const query =
      searchTerm
        .toLowerCase()
        .trim()

    return orders.filter(
      (order) => {
        const searchableText = [
          order.code,
          order.order_number,
          order.supplier_name,
          order.supplier_reference,
          order.purchase_request_code,
          order.warehouse_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()

        const matchesSearch =
          !query ||
          searchableText.includes(
            query
          )

        const matchesStatus =
          !statusFilter ||
          normalizeStatus(
            order.status
          ) ===
            normalizeStatus(
              statusFilter
            )

        return (
          matchesSearch &&
          matchesStatus
        )
      }
    )
  }, [
    orders,
    searchTerm,
    statusFilter,
  ])


  // =======================================================
  // Status appearance
  // =======================================================

  const getStatusColor = (
    status
  ) => {
    switch (
      normalizeStatus(status)
    ) {
      case "APPROVED":
      case "SENT":
      case "PARTIALLY_RECEIVED":
      case "RECEIVED":
      case "CLOSED":
        return "success"

      case "CANCELLED":
        return "error"

      case "DRAFT":
      default:
        return "default"
    }
  }


  const getStatusLabel = (
    status
  ) => {
    switch (
      normalizeStatus(status)
    ) {
      case "DRAFT":
        return "Brouillon"

      case "APPROVED":
        return "Approuvé"

      case "SENT":
        return "Envoyé"

      case "PARTIALLY_RECEIVED":
        return "Réception partielle"

      case "RECEIVED":
        return "Reçu"

      case "CLOSED":
        return "Clôturé"

      case "CANCELLED":
        return "Annulé"

      default:
        return status || "-"
    }
  }


  // =======================================================
  // Product label
  // =======================================================

  const getProductUnit = (
    item
  ) => {
    if (item.unit) {
      return item.unit
    }

    const product =
      products.find(
        (p) =>
          Number(p.id) ===
          Number(item.product_id)
      )

    return product?.unit || ""
  }


  // =======================================================
  // Render
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,
          pb: { xs: 2, md: 4 },
          "& .MuiPaper-root": {
            borderColor: "rgba(148, 163, 184, 0.24)",
            backgroundImage: "none",
          },
          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2.5,
          },
          "& .MuiTextField-root .MuiOutlinedInput-root": {
            borderRadius: 2.5,
          },
          "& .MuiAlert-root": {
            borderRadius: 3,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#f8fafc",
            color: "#334155",
            fontWeight: 800,
            whiteSpace: "nowrap",
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: "#f8fbff",
          },
          "& .MuiChip-root": {
            fontWeight: 700,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) auto",
            },
            alignItems: "start",
            columnGap: 2,
            rowGap: 1.25,
            mb: 2.5,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                color: "#0f172a",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.15rem",
                },
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
              }}
            >
              Bons de commande fournisseurs
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 760,
                lineHeight: 1.55,
              }}
            >
              Création, validation et suivi des commandes adressées aux fournisseurs.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "stretch",
                md: "flex-end",
              },
              justifySelf: {
                xs: "stretch",
                md: "end",
              },
              width: {
                xs: "100%",
                md: "auto",
              },
              pt: {
                xs: 0.5,
                md: 0.25,
              },
            }}
          >
            <Button
              variant="contained"
              startIcon={<ShoppingCartCheckoutRoundedIcon />}
              onClick={handleOpen}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 42,
                px: 2.25,
                borderRadius: 2.5,
                whiteSpace: "nowrap",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.18)",
              }}
            >
              Nouveau bon de commande
            </Button>
          </Box>
        </Box>



        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <MetricCard
            label="Bons de commande"
            value={orders.length}
            caption="Total enregistré"
          />
          <MetricCard
            label="À transmettre"
            value={
              orders.filter(
                (order) =>
                  normalizeStatus(order.status) === "APPROVED"
              ).length
            }
            caption="Approuvés, pas encore envoyés"
            accent="#f59e0b"
          />
          <MetricCard
            label="Réceptionnés"
            value={
              orders.filter((order) =>
                ["RECEIVED", "CLOSED"].includes(
                  normalizeStatus(order.status)
                )
              ).length
            }
            caption="Réception terminée"
            accent="#16a34a"
          />
          <MetricCard
            label="Valeur commandée"
            value={formatNumber(
              orders.reduce(
                (sum, order) =>
                  sum +
                  Number(
                    order.total_amount ??
                      order.grand_total ??
                      0
                  ),
                0
              )
            )}
            caption="Toutes devises confondues"
            accent="#0891b2"
          />
        </Box>



        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          Workflow : Demande d'achat approuvée → Bon de commande → Approbation → Envoi fournisseur → Réception fournisseur → Mise en stock.
        </Alert>


        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3.5,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={2}
          >
            <TextField
              fullWidth
              placeholder="Rechercher un bon, fournisseur, demande ou entrepôt..."
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(
                  event.target.value
                )
              }
            />

            <TextField
              select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              sx={{
                minWidth: 210,
              }}
            >
              <MenuItem value="">
                Tous statuts
              </MenuItem>

              <MenuItem value="DRAFT">
                Brouillon
              </MenuItem>

              <MenuItem value="APPROVED">
                Approuvé
              </MenuItem>

              <MenuItem value="SENT">
                Envoyé
              </MenuItem>

              <MenuItem value="PARTIALLY_RECEIVED">
                Réception partielle
              </MenuItem>

              <MenuItem value="RECEIVED">
                Reçu
              </MenuItem>

              <MenuItem value="CLOSED">
                Clôturé
              </MenuItem>

              <MenuItem value="CANCELLED">
                Annulé
              </MenuItem>
            </TextField>

            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm("")
                setStatusFilter("")
              }}
            >
              Reset
            </Button>
          </Stack>
        </Paper>


        <Paper
          sx={{
            overflow: "hidden",
            borderRadius: 3.5,
          }}
        >
          <Box
            sx={{
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1250,
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Bon
                  </TableCell>

                  <TableCell>
                    Demande source
                  </TableCell>

                  <TableCell>
                    Fournisseur
                  </TableCell>

                  <TableCell>
                    Entrepôt
                  </TableCell>

                  <TableCell>
                    Date
                  </TableCell>

                  <TableCell>
                    Livraison prévue
                  </TableCell>

                  <TableCell>
                    Devise
                  </TableCell>

                  <TableCell align="right">
                    Total
                  </TableCell>

                  <TableCell>
                    Statut
                  </TableCell>

                  <TableCell>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>


              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      Aucun bon de commande.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                    (order) => {
                      const status =
                        normalizeStatus(
                          order.status
                        )

                      return (
                        <TableRow
                          key={order.id}
                          hover
                        >
                          <TableCell>
                            <strong>
                              {order.code ||
                                order.order_number ||
                                `PO-${order.id}`}
                            </strong>
                          </TableCell>

                          <TableCell>
                            {order.purchase_request_code ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {order.supplier_name ||
                              `#${order.supplier_id}`}
                          </TableCell>

                          <TableCell>
                            {order.warehouse_code
                              ? `${order.warehouse_code} - ${order.warehouse_name}`
                              : order.warehouse_name ||
                                "-"}
                          </TableCell>

                          <TableCell>
                            {order.order_date
                              ? String(
                                  order.order_date
                                ).slice(
                                  0,
                                  10
                                )
                              : "-"}
                          </TableCell>

                          <TableCell>
                            {order.expected_delivery_date
                              ? String(
                                  order.expected_delivery_date
                                ).slice(
                                  0,
                                  10
                                )
                              : "-"}
                          </TableCell>

                          <TableCell>
                            {order.currency ||
                              "USD"}
                          </TableCell>

                          <TableCell align="right">
                            {formatNumber(
                              order.total_amount ??
                                order.grand_total ??
                                0
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={getStatusLabel(
                                status
                              )}
                              color={getStatusColor(
                                status
                              )}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {status ===
                                "DRAFT" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() =>
                                    runAction(
                                      () =>
                                        approvePurchaseOrder(
                                          order.id,
                                          {}
                                        ),
                                      "Bon de commande approuvé."
                                    )
                                  }
                                >
                                  Approuver
                                </Button>
                              )}

                              {status ===
                                "APPROVED" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() =>
                                    runAction(
                                      () =>
                                        sendPurchaseOrder(
                                          order.id,
                                          {}
                                        ),
                                      "Bon envoyé au fournisseur."
                                    )
                                  }
                                >
                                  Envoyer
                                </Button>
                              )}

                              {![
                                "RECEIVED",
                                "CLOSED",
                                "CANCELLED",
                              ].includes(
                                status
                              ) && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() =>
                                    runAction(
                                      () =>
                                        cancelPurchaseOrder(
                                          order.id,
                                          {}
                                        ),
                                      "Bon de commande annulé."
                                    )
                                  }
                                >
                                  Annuler
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  )
                )}
              </TableBody>
            </Table>
          </Box>
        

          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                minHeight: 54,
                px: { xs: 1, sm: 2 },
              },
            }}
          />
        </Paper>
      </Box>


      {/* ================================================= */}
      {/* DIALOG */}
      {/* ================================================= */}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
      
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            maxHeight: { xs: "100dvh", sm: "90dvh" },
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle>
          Nouveau bon de commande
        </DialogTitle>


        <DialogContent>
          <Stack
            spacing={2.5}
            mt={1}
          >
            {selectedSourceRequest && (
              <Alert
                severity="success"
                icon={
                  <LockRoundedIcon />
                }
              >
                <strong>
                  Demande source :{" "}
                  {selectedSourceRequest.code}
                </strong>

                {" — "}

                les produits, quantités et l'entrepôt proviennent automatiquement de la demande d'achat approuvée.
              </Alert>
            )}


            {/* ----------------------------------------- */}
            {/* Organisation / Supplier / Warehouse */}
            {/* ----------------------------------------- */}

            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={2}
            >
              <TextField
                select
                label="Organisation"
                name="organization_id"
                value={
                  formData.organization_id
                }
                onChange={handleChange}
                fullWidth
                disabled={
                  Boolean(
                    selectedSourceRequest
                  )
                }
              >
                {organizations.length ? (
                  organizations.map(
                    (organization) => (
                      <MenuItem
                        key={
                          organization.id
                        }
                        value={
                          organization.id
                        }
                      >
                        {organization.code
                          ? `${organization.code} - ${organization.name}`
                          : organization.name}
                      </MenuItem>
                    )
                  )
                ) : (
                  <MenuItem value={1}>
                    Organisation principale
                  </MenuItem>
                )}
              </TextField>


              <TextField
                select
                required
                label="Fournisseur"
                name="supplier_id"
                value={
                  formData.supplier_id
                }
                onChange={handleChange}
                fullWidth
              >
                {suppliers.map(
                  (supplier) => (
                    <MenuItem
                      key={supplier.id}
                      value={supplier.id}
                    >
                      {supplier.code
                        ? `${supplier.code} - ${supplier.name}`
                        : supplier.name}
                    </MenuItem>
                  )
                )}
              </TextField>


              <Tooltip
                title={
                  selectedSourceRequest
                    ? "L'entrepôt a été défini dans la demande d'achat approuvée."
                    : ""
                }
              >
                <TextField
                  select
                  required
                  label="Entrepôt de réception"
                  name="warehouse_id"
                  value={
                    formData.warehouse_id
                  }
                  onChange={handleChange}
                  disabled={
                    Boolean(
                      selectedSourceRequest
                    )
                  }
                  fullWidth
                >
                  {warehouses.map(
                    (warehouse) => (
                      <MenuItem
                        key={warehouse.id}
                        value={warehouse.id}
                      >
                        {warehouse.code
                          ? `${warehouse.code} - ${warehouse.name}`
                          : warehouse.name}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Tooltip>
            </Stack>


            {/* ----------------------------------------- */}
            {/* Purchase request */}
            {/* ----------------------------------------- */}

            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={2}
            >
              <Tooltip
                title={
                  selectedSourceRequest
                    ? "Cette demande est la source du bon de commande et ne peut plus être changée."
                    : ""
                }
              >
                <TextField
                  select
                  required
                  label="Demande d'achat source"
                  name="purchase_request_id"
                  value={
                    formData.purchase_request_id
                  }
                  onChange={
                    handlePurchaseRequestChange
                  }
                  disabled={
                    Boolean(
                      selectedSourceRequest
                    )
                  }
                  fullWidth
                  helperText={
                    selectedSourceRequest
                      ? "Demande approuvée verrouillée"
                      : "Seules les demandes approuvées et non encore converties sont proposées"
                  }
                >
                  {requests.map(
                    (request) => (
                      <MenuItem
                        key={request.id}
                        value={request.id}
                      >
                        {request.code ||
                          request.request_number ||
                          `PR-${request.id}`}
                      </MenuItem>
                    )
                  )}

                  {selectedSourceRequest &&
                    !requests.some(
                      (request) =>
                        Number(
                          request.id
                        ) ===
                        Number(
                          selectedSourceRequest.id
                        )
                    ) && (
                      <MenuItem
                        value={
                          selectedSourceRequest.id
                        }
                      >
                        {
                          selectedSourceRequest.code
                        }
                      </MenuItem>
                    )}
                </TextField>
              </Tooltip>


              <TextField
                label="Référence fournisseur"
                name="supplier_reference"
                value={
                  formData.supplier_reference
                }
                onChange={handleChange}
                fullWidth
                placeholder="Ex. REF-FOUR-2026-015"
              />
            </Stack>


            {/* ----------------------------------------- */}
            {/* Dates / Currency */}
            {/* ----------------------------------------- */}

            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={2}
            >
              <TextField
                type="date"
                label="Date commande"
                name="order_date"
                value={
                  formData.order_date
                }
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />


              <TextField
                type="date"
                label="Livraison prévue"
                name="expected_delivery_date"
                value={
                  formData.expected_delivery_date
                }
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />


              <TextField
                select
                label="Devise"
                name="currency"
                value={
                  formData.currency
                }
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="USD">
                  USD
                </MenuItem>

                <MenuItem value="CDF">
                  CDF
                </MenuItem>
              </TextField>


              <TextField
                type="number"
                label="Taux CDF / USD"
                name="exchange_rate"
                value={
                  formData.exchange_rate
                }
                onChange={handleChange}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                fullWidth
              />
            </Stack>


            <Divider />


            {/* ----------------------------------------- */}
            {/* Products title */}
            {/* ----------------------------------------- */}

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "stretch",
                sm: "center",
              }}
              spacing={1}
            >
              <Box>
                <Typography variant="h6">
                  Produits commandés
                </Typography>

                {selectedSourceRequest && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Les produits et quantités proviennent de{" "}
                    <strong>
                      {
                        selectedSourceRequest.code
                      }
                    </strong>
                    . Vous renseignez ici le prix réellement négocié avec le fournisseur.
                  </Typography>
                )}
              </Box>


              {!selectedSourceRequest && (
                <Button
                  startIcon={
                    <AddRoundedIcon />
                  }
                  onClick={addItem}
                >
                  Ajouter produit
                </Button>
              )}
            </Stack>


            {/* ----------------------------------------- */}
            {/* Product lines */}
            {/* ----------------------------------------- */}

            {formData.items.map(
              (item, index) => {
                const unit =
                  getProductUnit(item)

                return (
                  <Paper
                    key={`${item.product_id}-${index}`}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3.5,
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        lg: "row",
                      }}
                      spacing={1.5}
                      alignItems={{
                        xs: "stretch",
                        lg: "center",
                      }}
                    >
                      <TextField
                        select
                        required
                        label="Produit"
                        value={
                          item.product_id
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "product_id",
                            event.target
                              .value
                          )
                        }
                        disabled={
                          Boolean(
                            selectedSourceRequest
                          )
                        }
                        fullWidth
                      >
                        {products.map(
                          (product) => (
                            <MenuItem
                              key={
                                product.id
                              }
                              value={
                                product.id
                              }
                            >
                              {product.code
                                ? `${product.code} - ${product.name}`
                                : product.name}
                            </MenuItem>
                          )
                        )}
                      </TextField>


                      <TextField
                        type="number"
                        label={
                          unit
                            ? `Quantité (${unit})`
                            : "Quantité"
                        }
                        value={
                          item.ordered_quantity
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "ordered_quantity",
                            event.target
                              .value
                          )
                        }
                        inputProps={{
                          min: 0.001,
                          step: "any",
                        }}
                        disabled={
                          Boolean(
                            selectedSourceRequest
                          )
                        }
                        sx={{
                          minWidth: 170,
                        }}
                      />


                      <TextField
                        type="number"
                        label={
                          unit
                            ? `Coût / ${unit}`
                            : "Coût unitaire"
                        }
                        value={
                          item.unit_cost
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "unit_cost",
                            event.target
                              .value
                          )
                        }
                        inputProps={{
                          min: 0,
                          step: "0.01",
                        }}
                        helperText={
                          selectedSourceRequest
                            ? "Prix fournisseur"
                            : ""
                        }
                        sx={{
                          minWidth: 175,
                        }}
                      />


                      <TextField
                        type="number"
                        label="Remise %"
                        value={
                          item.discount_percent
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "discount_percent",
                            event.target
                              .value
                          )
                        }
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: "0.01",
                        }}
                        sx={{
                          minWidth: 130,
                        }}
                      />


                      <TextField
                        type="number"
                        label="Taxe %"
                        value={
                          item.tax_percent
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "tax_percent",
                            event.target
                              .value
                          )
                        }
                        inputProps={{
                          min: 0,
                          step: "0.01",
                        }}
                        sx={{
                          minWidth: 120,
                        }}
                      />


                      {!selectedSourceRequest && (
                        <IconButton
                          color="error"
                          onClick={() =>
                            removeItem(
                              index
                            )
                          }
                          disabled={
                            formData.items
                              .length === 1
                          }
                          aria-label="Supprimer le produit"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>


                    {selectedSourceRequest &&
                      unit && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display:
                              "block",
                            mt: 1,
                          }}
                        >
                          Quantité approuvée :{" "}
                          <strong>
                            {
                              item.ordered_quantity
                            }{" "}
                            {unit}
                          </strong>
                        </Typography>
                      )}
                  </Paper>
                )
              }
            )}


            <Divider />


            {/* ----------------------------------------- */}
            {/* Other amounts */}
            {/* ----------------------------------------- */}

            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={2}
            >
              <TextField
                type="number"
                label="Remise globale"
                name="discount_amount"
                value={
                  formData.discount_amount
                }
                onChange={handleChange}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                fullWidth
              />


              <TextField
                type="number"
                label="Taxes globales"
                name="tax_amount"
                value={
                  formData.tax_amount
                }
                onChange={handleChange}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                fullWidth
              />


              <TextField
                type="number"
                label="Transport"
                name="transport_cost"
                value={
                  formData.transport_cost
                }
                onChange={handleChange}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                fullWidth
              />


              <TextField
                type="number"
                label="Autres frais"
                name="other_cost"
                value={
                  formData.other_cost
                }
                onChange={handleChange}
                inputProps={{
                  min: 0,
                  step: "0.01",
                }}
                fullWidth
              />
            </Stack>


            <TextField
              multiline
              minRows={2}
              label="Notes"
              name="notes"
              value={
                formData.notes
              }
              onChange={handleChange}
              fullWidth
            />


            {/* ----------------------------------------- */}
            {/* Totals */}
            {/* ----------------------------------------- */}

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor:
                  "background.default",
              }}
            >
              <Stack
                spacing={0.5}
              >
                <Typography>
                  Sous-total calculé :{" "}
                  <strong>
                    {formatNumber(
                      itemsSubtotal
                    )}{" "}
                    {
                      formData.currency
                    }
                  </strong>
                </Typography>


                <Typography
                  variant="h6"
                >
                  Total commande :{" "}
                  <strong>
                    {formatNumber(
                      grandTotal
                    )}{" "}
                    {
                      formData.currency
                    }
                  </strong>
                </Typography>


                {selectedSourceRequest && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Ce montant correspond au prix réellement convenu avec le fournisseur. La demande d'achat reste le document d'origine du besoin.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
          >
            {saving
              ? "Enregistrement..."
              : "Créer le bon"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* ================================================= */}
      {/* Snackbar */}
      {/* ================================================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setSnackbar(
            (prev) => ({
              ...prev,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (prev) => ({
                ...prev,
                open: false,
              })
            )
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default PurchaseOrdersPage